// One catalog for every AI model the app can run, on either provider
// (ANTHROPIC_API_PROVIDER_PLAN D5). Holds the dated rate table, the provider
// lookup, and the cost-estimate functions used by the API (usage telemetry,
// monthly ceiling) and the web app (admin pickers, what-if repricing).
// Pure data and functions only: this module is bundled into the SPA, so it must
// never import an SDK (D9).

import type { AllowedAiModelId } from "./types.js";

export type AiProvider = "bedrock" | "anthropic";

/** USD per million tokens. */
export interface AiModelRates {
  input: number;
  output: number;
  cacheWrite: number;
  cacheRead: number;
}

export interface AiModelSpec {
  id: AllowedAiModelId;
  label: string;
  provider: AiProvider;
  rates: AiModelRates;
  /** Tokens for the same text relative to Claude Haiku 4.5's tokenizer (estimates only). */
  tokenizerFactor: number;
  /** Output multiplier for default adaptive thinking at the D7 effort (estimates only). */
  thinkingOutputFactor: number;
}

export interface UsageTotals {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
}

/** Date the rates below were last checked against provider price lists. */
export const AI_RATES_CHECKED_ON = "2026-10-11";

// Typed against every allowlisted model id (D-R8) so a newly allowlisted model
// without a catalog row is a compile error. Bedrock rates are us-east-1 on-demand
// global-endpoint rates; the `us.` regional endpoint would be ~10% higher.
// Estimates only: provider invoices remain authoritative.
export const AI_MODELS: Record<AllowedAiModelId, AiModelSpec> = {
  "anthropic.claude-haiku-4-5": {
    id: "anthropic.claude-haiku-4-5", label: "Claude Haiku 4.5", provider: "bedrock",
    rates: { input: 1, output: 5, cacheWrite: 1.25, cacheRead: 0.1 }, tokenizerFactor: 1, thinkingOutputFactor: 1,
  },
  "anthropic.claude-sonnet-4-6": {
    id: "anthropic.claude-sonnet-4-6", label: "Claude Sonnet 4.6", provider: "bedrock",
    rates: { input: 3, output: 15, cacheWrite: 3.75, cacheRead: 0.3 }, tokenizerFactor: 1, thinkingOutputFactor: 1,
  },
  "anthropic.claude-sonnet-5": {
    id: "anthropic.claude-sonnet-5", label: "Claude Sonnet 5", provider: "bedrock",
    rates: { input: 2, output: 10, cacheWrite: 2.5, cacheRead: 0.2 }, tokenizerFactor: 1.3, thinkingOutputFactor: 1.5,
  },
  "amazon.nova-micro-v1:0": {
    id: "amazon.nova-micro-v1:0", label: "Nova Micro", provider: "bedrock",
    rates: { input: 0.035, output: 0.14, cacheWrite: 0.035, cacheRead: 0.0035 }, tokenizerFactor: 1, thinkingOutputFactor: 1,
  },
  "amazon.nova-pro-v1:0": {
    id: "amazon.nova-pro-v1:0", label: "Nova Pro", provider: "bedrock",
    rates: { input: 0.8, output: 3.2, cacheWrite: 0.8, cacheRead: 0.08 }, tokenizerFactor: 1, thinkingOutputFactor: 1,
  },
  "claude-opus-5-5": {
    id: "claude-opus-5-5", label: "Claude Opus 5.5", provider: "anthropic",
    rates: { input: 4, output: 20, cacheWrite: 5, cacheRead: 0.2 }, tokenizerFactor: 1.3, thinkingOutputFactor: 1.5,
  },
  "claude-sonnet-5-5": {
    id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5", provider: "anthropic",
    rates: { input: 2, output: 10, cacheWrite: 2.5, cacheRead: 0.2 }, tokenizerFactor: 1.3, thinkingOutputFactor: 1.5,
  },
  "claude-haiku-5-5": {
    id: "claude-haiku-5-5", label: "Claude Haiku 5.5", provider: "anthropic",
    rates: { input: 0.1, output: 0.5, cacheWrite: 0.125, cacheRead: 0.01 }, tokenizerFactor: 1.3, thinkingOutputFactor: 1.5,
  },
};

export function aiModelSpec(id: string): AiModelSpec | undefined {
  return Object.prototype.hasOwnProperty.call(AI_MODELS, id) ? AI_MODELS[id as AllowedAiModelId] : undefined;
}

/** D1: the provider follows from the model id. */
export function providerForModelId(id: string): AiProvider {
  const spec = aiModelSpec(id);
  if (spec) return spec.provider;
  return /^claude-/.test(id) ? "anthropic" : "bedrock";
}

export function providerLabel(p: AiProvider): string {
  return p === "anthropic" ? "Anthropic API" : "Amazon Bedrock";
}

// D-R13: an unrecognized model id is priced at the MOST EXPENSIVE table rate, never
// at Haiku's -- underpricing an unbudgeted model is the failure mode that costs
// money, not the one that saves it.
const MOST_EXPENSIVE_RATE: AiModelRates = Object.values(AI_MODELS).reduce(
  (max, m) => (m.rates.output > max.output ? m.rates : max),
  Object.values(AI_MODELS)[0]!.rates
);

const loggedMissingRates = new Set<string>();

function ratesFor(modelId: string): AiModelRates {
  const known = aiModelSpec(modelId);
  if (known) return known.rates;
  if (!loggedMissingRates.has(modelId)) {
    loggedMissingRates.add(modelId);
    console.error(JSON.stringify({ msg: "ai_rate_missing", modelId }));
  }
  return MOST_EXPENSIVE_RATE;
}

/** Microdollars (USD x 1e6). Rates are USD per MTok, so tokens x rate is already micros. */
export function estimateCostMicros(
  modelId: string,
  inputTokens: number,
  outputTokens: number,
  cacheReadTokens = 0,
  cacheCreationTokens = 0
): number {
  const r = ratesFor(modelId);
  return Math.round(
    inputTokens * r.input +
      outputTokens * r.output +
      cacheReadTokens * r.cacheRead +
      cacheCreationTokens * r.cacheWrite
  );
}

/** Representative usage, measured against the Haiku 4.5 tokenizer (D19). */
export const AI_USAGE_PROFILES: {
  generation: UsageTotals;
  chatConversation: UsageTotals;
} = {
  generation: { inputTokens: 120, outputTokens: 350, cacheReadTokens: 0, cacheCreationTokens: 0 },
  chatConversation: { inputTokens: 15000, outputTokens: 4000, cacheReadTokens: 70000, cacheCreationTokens: 9300 },
};

function usdFor(modelId: string, t: UsageTotals): number {
  return estimateCostMicros(modelId, t.inputTokens, t.outputTokens, t.cacheReadTokens, t.cacheCreationTokens) / 1_000_000;
}

function scaledProfile(id: string, profile: keyof typeof AI_USAGE_PROFILES): UsageTotals {
  const spec = aiModelSpec(id);
  const tok = spec?.tokenizerFactor ?? 1;
  const think = spec?.thinkingOutputFactor ?? 1;
  const p = AI_USAGE_PROFILES[profile];
  return {
    inputTokens: p.inputTokens * tok,
    outputTokens: p.outputTokens * tok * think,
    cacheReadTokens: p.cacheReadTokens * tok,
    cacheCreationTokens: p.cacheCreationTokens * tok,
  };
}

/** Profile tokens x tokenizerFactor (output also x thinkingOutputFactor), priced at id's rates. */
export function estimateProfileUsd(id: string, profile: keyof typeof AI_USAGE_PROFILES): number {
  return usdFor(id, scaledProfile(id, profile));
}

/**
 * Re-price recorded totals at another model's rates (AI costs what-if). Totals are
 * as recorded under fromModelId; the tokenizer ratio between the two models is
 * applied to every count, and output also carries the thinking-factor ratio so a
 * non-thinking model re-priced onto a thinking one is not under-estimated.
 * Real thinking and answer length vary by model: a rough guide only.
 */
export function repriceUsageUsd(totals: UsageTotals, fromModelId: string, toModelId: string): number {
  const from = aiModelSpec(fromModelId);
  const to = aiModelSpec(toModelId);
  const tok = (to?.tokenizerFactor ?? 1) / (from?.tokenizerFactor ?? 1);
  const think = (to?.thinkingOutputFactor ?? 1) / (from?.thinkingOutputFactor ?? 1);
  return usdFor(toModelId, {
    inputTokens: totals.inputTokens * tok,
    outputTokens: totals.outputTokens * tok * think,
    cacheReadTokens: totals.cacheReadTokens * tok,
    cacheCreationTokens: totals.cacheCreationTokens * tok,
  });
}

/**
 * USD for N tokens/day at a model's blended chat rate (admin cap hint). Divides by
 * the SCALED profile token total because the budget counts the model's own tokens.
 */
export function dailyTokenCapUsd(id: string, tokens: number): number {
  const scaled = scaledProfile(id, "chatConversation");
  const total = scaled.inputTokens + scaled.outputTokens + scaled.cacheReadTokens + scaled.cacheCreationTokens;
  if (total <= 0) return 0;
  return (usdFor(id, scaled) / total) * tokens;
}
